import { atom } from "jotai";
import { Node, getNodesBounds } from "@xyflow/react";

import { edgesAtom } from "../edges";
import { graphlib, layout } from "dagre";
import { nodesAtom } from "../nodes";
import {
  NO_AUTO_LAYOUT_NODE_TYPES,
  NODE_SEP,
  RANK_SEP,
} from "@zapier/canvas-constants";
import { takeSnapshotSetter } from "./history";
import { zapStepNodesVisibleAtomFamily } from "../zaps";
import { adjustYPositionForStraightLine } from "../utils";
import { graphDirectionAtom } from "./graphDirectionAtom";

/* IMPORTANT! Any changes made here, please make them for autoLayoutSelectedNodesSetter as well */
export const autoLayoutSetter = atom(null, (get, set) => {
  set(takeSnapshotSetter);
  const graphDirection = get(graphDirectionAtom);

  const dagreGraph = new graphlib.Graph();

  dagreGraph.setGraph({
    rankdir: graphDirection,
    ranksep: RANK_SEP,
    nodesep: NODE_SEP,
  });

  dagreGraph.setDefaultEdgeLabel(() => ({}));

  /** IMPORTANT! Need to filter out zapStepEdge or else dagre graph will be off */
  const edges = get(edgesAtom).filter((edge) => edge.type !== "zapStepEdge");
  const nodes = get(nodesAtom);

  // Don't auto layout default nodes in a group
  const nodesToAutoLayout = nodes.filter(
    (node) => !NO_AUTO_LAYOUT_NODE_TYPES.includes(node.type!) && !node.parentId,
  );

  function calcNodeHeight(node: Node) {
    if (node.type === "zap") {
      const visibleZapSteps = get(zapStepNodesVisibleAtomFamily(node.id));
      const bounds = getNodesBounds([node, ...visibleZapSteps]);
      return bounds.height;
    }

    return node.measured?.height;
  }

  nodesToAutoLayout.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: node.measured?.width,
      height: calcNodeHeight(node),
    });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  layout(dagreGraph);

  const updatedNodes = nodes.map((node) => {
    const layoutedNode = dagreGraph.node(node.id);

    if (layoutedNode) {
      return {
        ...node,
        position: {
          x: layoutedNode.x,
          y:
            graphDirection === "LR"
              ? adjustYPositionForStraightLine({
                  node,
                  nodes,
                  edges,
                  dagreGraph,
                  layoutedNode,
                })
              : layoutedNode.y,
        },
      };
    }
    return node;
  });

  set(nodesAtom, updatedNodes);

  return { nodes: updatedNodes, isSuccess: true };
});

autoLayoutSetter.debugLabel = "autoLayoutSetter";
